import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronUp, PackagePlus, X } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { ProductGrid } from "@/components/pos/ProductGrid";
import { CartPanel } from "@/components/pos/CartPanel";
import { DiscountDialog } from "@/components/pos/DiscountDialog";
import { PaymentDialog } from "@/components/pos/PaymentDialog";
import { SaleCompleteDialog } from "@/components/pos/SaleCompleteDialog";
import { QuickAddDialog } from "@/components/QuickAddDialog";
import { useAuth, useCan } from "@/context/AuthContext";
import { usePrint } from "@/context/PrintContext";
import { usePosCart } from "@/hooks/usePosCart";
import { useBarcodeScanner } from "@/hooks/useBarcodeScanner";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { api, errMsg } from "@/lib/api";
import { formatMoney } from "@/lib/money";
import { CameraScanButton } from "@/components/scan/CameraScanButton";
import { codeVariants, prefetchScanner } from "@/lib/cameraScan";
import { isOnline, onConnectivity, useOnline } from "@/lib/connectivity";
import { applyPending, device, isWellFormedSale, kvGet, kvSet, productsKey, utcToday } from "@/lib/localdb";
import { onSynced, saveOfflineSale, useOutbox } from "@/lib/outbox";
import { ensureBlock, peekNumber, takeNumber, resetBlocks } from "@/lib/receiptBlocks";
import { SyncIndicator } from "@/components/Offline";

function NotFoundToast({ id, code, onQuickAdd, canAdd, online }) {
  return (
    <div className="flex w-[340px] max-w-[92vw] items-center gap-3 rounded-xl border border-rose-200 bg-white p-3 shadow-lg" data-testid="product-not-found-toast">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-rose-700">Product not found</div>
        <div className="truncate font-mono text-xs text-slate-500" data-testid="product-not-found-code">{code}</div>
      </div>
      {canAdd && <button
        onClick={() => { toast.dismiss(id); onQuickAdd(code); }}
        disabled={!online}
        data-testid="not-found-quick-add-button"
        className="flex h-10 items-center gap-1.5 rounded-lg bg-slate-900 px-3 text-sm font-semibold text-white disabled:bg-slate-300"
      >
        <PackagePlus className="h-4 w-4" /> {online ? "Quick add" : "Needs internet"}
      </button>}
      <button onClick={() => toast.dismiss(id)} className="grid h-8 w-8 place-items-center text-slate-400" aria-label="Dismiss"><X className="h-4 w-4" /></button>
    </div>
  );
}

export default function POS() {
  const { session } = useAuth();
  const biz = session.business;
  const pharmacy = biz.business_type === "pharmacy";
  const online = useOnline();
  const outbox = useOutbox();
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(false);
  const [pay, setPay] = useState(false);
  const [discount, setDiscount] = useState(null);
  const [done, setDone] = useState(null);
  const [quick, setQuick] = useState({ open: false, barcode: "", addAfter: false });
  const [nextNo, setNextNo] = useState("");
  const searchRef = useRef(null);
  const desktop = useMediaQuery("(min-width: 768px)");
  const can = useCan();
  const { printSale } = usePrint();
  const cacheKey = productsKey(session);

  // Local store first (same view online and offline), then refresh from the server while online.
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/products");
      if (!session.impersonation) await kvSet(cacheKey, data);
      setRaw(data);
      return data;
    } catch (e) {
      if (e.response) toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [cacheKey, session.impersonation]);

  const loadNext = useCallback(async () => {
    if (session.impersonation) return api.get("/sales-meta/next-receipt").then((r) => setNextNo(r.data.receipt_code)).catch(() => {});
    if (isOnline()) await ensureBlock(session.branch.id);
    setNextNo(await peekNumber(session.branch));
  }, [session.branch, session.impersonation]);

  useEffect(() => {
    kvGet(cacheKey).then((c) => {
      if (c) {
        setRaw((r) => r || c);
        setLoading(false);
      }
    });
    if (isOnline()) load();
    loadNext();
    const tick = () => isOnline() && load();
    const iv = setInterval(tick, 60000);
    window.addEventListener("focus", tick);
    const offSync = onSynced(() => load());
    const offConn = onConnectivity((up) => {
      loadNext();
      if (up) load();
    });
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", tick);
      offSync();
      offConn();
    };
  }, [cacheKey, load, loadNext]);

  const data = useMemo(() => {
    const v = applyPending(raw || { today: utcToday(), products: [] }, outbox.items, session.branch.id, pharmacy);
    return online ? v : { ...v, today: utcToday() };
  }, [raw, outbox.items, session.branch.id, pharmacy, online]);

  const productsById = useMemo(() => Object.fromEntries(data.products.map((p) => [p.id, p])), [data.products]);
  const byBarcode = useMemo(() => Object.fromEntries(data.products.filter((p) => p.barcode).map((p) => [p.barcode, p])), [data.products]);
  const cart = usePosCart({ productsById, business: biz, today: data.today, inventory: !!data.inventory });

  const addProduct = (p) => {
    const err = cart.add(p);
    if (err) toast.error(err, { id: "cart-msg" });
  };

  const lastLocal = () => outbox.items.find((i) => i.branch_id === session.branch.id && isWellFormedSale(i.sale));
  const reprintLast = async () => {
    if (!isOnline()) {
      const l = lastLocal();
      return l ? printSale(l.sale) : toast.error("No sales saved on this device yet");
    }
    try {
      const { data: sale } = await api.post("/sales/last/reprint");
      printSale(sale);
    } catch (e) {
      if (!e.response && lastLocal()) return printSale(lastLocal().sale);
      toast.error(e?.response?.status === 404 ? "No sales yet in this branch" : errMsg(e));
    }
  };

  const openQuick = (barcode, addAfter = false) => setQuick({ open: true, barcode, addAfter });

  const onScan = (code) => {
    const p = byBarcode[code];
    if (p) return addProduct(p);
    toast.custom((id) => <NotFoundToast id={id} code={code} canAdd={can("products")} online={isOnline()} onQuickAdd={(c) => openQuick(c, true)} />, { id: "not-found", duration: 6000 });
  };
  useBarcodeScanner(onScan);
  useEffect(prefetchScanner, []);
  // camera detections go through the exact keyboard-wedge handler (onScan); UPC-A/EAN-13 variants are tried first
  const onCamera = (raw) => {
    const code = codeVariants(raw).find((c) => byBarcode[c]) || raw;
    const p = byBarcode[code];
    onScan(code);
    if (p) return { ok: true, label: p.name };
    return { ok: false, label: "Product not found", action: can("products") && isOnline() ? { label: "Quick add", run: () => openQuick(code, true) } : null };
  };

  const onSearchEnter = (q, shown) => {
    if (!q) return false;
    if (byBarcode[q]) {
      addProduct(byBarcode[q]);
      return true;
    }
    if (shown.length === 1) {
      addProduct(shown[0]);
      return true;
    }
    if (shown.length === 0 && /^\d{4,}$/.test(q)) onScan(q);
    return false;
  };

  const charge = () => {
    if (!cart.rows.length) return;
    setSheet(false);
    setPay(true);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "F2") {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === "F9" && !pay && !done) {
        e.preventDefault();
        charge();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Every sale (online or offline) takes its receipt number from this device's reserved block, so a lost response can't diverge.
  const complete = async (payment) => {
    const clientSaleId = crypto.randomUUID();
    const imp = !!session.impersonation; // admin "log in as": server-assigned numbers, no device block, no offline save
    if (isOnline() && !imp) await ensureBlock(session.branch.id);
    const numbers = imp ? { receipt_no: null, receipt_code: null } : await takeNumber(session.branch);
    const saveLocal = async () => {
      const sale = await saveOfflineSale({ cart, payment, session, clientSaleId, numbers });
      cart.clear();
      setPay(false);
      setDone(sale);
      loadNext();
      return true;
    };
    const viaCn = !!payment.credit_note;
    if (!isOnline() && viaCn) { toast.error("Needs internet"); return false; }
    if (!isOnline() && !imp) return saveLocal();
    const post = (nums) => api.post("/sales", {
      lines: cart.lines.map((l) => ({ product_id: l.product_id, qty: l.qty, discount: l.discount })),
      cart_discount: cart.cartDiscount,
      payment,
      expected_total: cart.totals.total,
      client_sale_id: clientSaleId,
      ...(nums ? { receipt_no: nums.receipt_no, receipt_code: nums.receipt_code, device_id: device().id } : {}),
    }, { timeout: 10000 });
    try {
      let res;
      try {
        res = await post(imp ? null : numbers);
      } catch (e) {
        // A stale local block: drop it and let the server number this sale.
        if (imp || !/not reserved for this device/.test(errMsg(e))) throw e;
        await resetBlocks(session.branch.id);
        res = await post(null);
      }
      const sale = res.data;
      cart.clear();
      setPay(false);
      setDone(sale);
      loadNext();
      load();
      if (!imp) ensureBlock(session.branch.id);
      return true;
    } catch (e) {
      if (!e.response && !imp && !viaCn) return saveLocal();
      toast.error(errMsg(e));
      load();
      return false;
    }
  };

  const [pendingAdd, setPendingAdd] = useState(null);
  useEffect(() => {
    if (pendingAdd && productsById[pendingAdd]) {
      setPendingAdd(null);
      addProduct(productsById[pendingAdd]);
    }
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const onCreated = async (p) => {
    toast.success(`${p.name} added`);
    await load();
    if (quick.addAfter) setPendingAdd(p.id);
  };

  const lineDiscount = (row) =>
    setDiscount({ title: `Discount · ${row.product.name}`, current: row.discount, apply: (d) => cart.setLineDiscount(row.product_id, d) });
  const cartDiscount = () => setDiscount({ title: "Cart discount", current: cart.cartDiscount, apply: (d) => cart.setCartDiscount(d) });

  const panel = (
    <CartPanel
      cart={cart}
      biz={biz}
      onCharge={charge}
      onLineDiscount={lineDiscount}
      onCartDiscount={cartDiscount}
      onToast={(m) => toast.error(m, { id: "cart-msg" })}
      canDiscount={can("discounts")}
      branchName={session.branch.name}
      receiptPreviewNo={nextNo}
    />
  );
  const total = cart.totals?.total || 0;

  return (
    <div className="flex h-full" data-testid="pos-screen">
      <section className={`min-w-0 flex-1 overflow-y-auto ${desktop ? "" : "pb-24"}`} data-testid="pos-products-section">
        {loading ? (
          <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 12 }).map((_, i) => <div key={i} className="h-[124px] animate-pulse rounded-xl bg-slate-200/70" />)}
          </div>
        ) : (
          <ProductGrid ref={searchRef} cameraSlot={<CameraScanButton continuous title="Scan products" testid="pos-camera-button" onDetect={onCamera} status={{ count: cart.rows.reduce((n, r) => n + Number(r.qty || 0), 0), total: `${biz.currency} ${formatMoney(cart.totals?.total || 0, biz.decimals)}` }} />} products={data.products} biz={biz} today={data.today} onAdd={addProduct} onSearchEnter={onSearchEnter} onQuickAdd={can("products") ? (c) => openQuick(c) : null} onReprintLast={can("reprint") ? reprintLast : null} />
        )}
      </section>

      {desktop ? (
        <aside className="grain flex w-[340px] shrink-0 flex-col bg-slate-900 lg:w-[400px]" data-testid="pos-cart-aside">{panel}</aside>
      ) : (
        <>
          <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-800 bg-slate-950 px-3 pb-[max(env(safe-area-inset-bottom),12px)] pt-3" data-testid="mobile-charge-bar">
            <div className="flex items-center gap-2">
              <button onClick={() => setSheet(true)} data-testid="mobile-cart-toggle" className="flex h-14 min-w-0 flex-1 items-center gap-3 rounded-xl bg-white/5 px-3 text-left text-white">
                <span className="grid h-8 min-w-[32px] place-items-center rounded-lg bg-amber-500 px-1.5 font-mono text-sm font-bold text-slate-950" data-testid="mobile-cart-count">{cart.itemCount}</span>
                <span className="min-w-0">
                  <span className="block text-[11px] uppercase tracking-widest text-slate-400">View cart</span>
                  <span className="money block truncate text-base font-bold" data-testid="mobile-cart-total">{biz.currency} {formatMoney(total, biz.decimals)}</span>
                </span>
                <ChevronUp className="ml-auto h-5 w-5 text-slate-400" />
              </button>
              <button onClick={charge} disabled={!cart.rows.length} data-testid="mobile-charge-button" className="h-14 shrink-0 rounded-xl bg-amber-500 px-6 font-display text-lg font-bold text-slate-950 disabled:bg-slate-700 disabled:text-slate-500">
                Charge
              </button>
            </div>
          </div>
          <Sheet open={sheet} onOpenChange={setSheet}>
            <SheetContent side="bottom" className="h-[88dvh] rounded-t-2xl border-0 bg-slate-900 p-0" data-testid="mobile-cart-sheet">
              <SheetTitle className="sr-only">Cart</SheetTitle>
              <SheetDescription className="sr-only">Current sale</SheetDescription>
              <div className="mx-auto mt-2 h-1.5 w-12 rounded-full bg-slate-700" />
              <div className="h-[calc(88dvh-14px)]">{panel}</div>
            </SheetContent>
          </Sheet>
        </>
      )}

      <SyncIndicator />
      <DiscountDialog target={discount} biz={biz} onApply={(d) => discount.apply(d)} onClose={() => setDiscount(null)} />
      <PaymentDialog open={pay} onOpenChange={setPay} total={total} biz={biz} onComplete={complete} />
      <SaleCompleteDialog sale={done} biz={biz} onClose={() => setDone(null)} />
      <QuickAddDialog open={quick.open} onOpenChange={(o) => setQuick((q) => ({ ...q, open: o }))} biz={biz} initialBarcode={quick.barcode} onCreated={onCreated} />
    </div>
  );
}
